// Ajustes rápidos al pie del panel de "Elegí el producto": Calidad, Fondo y Formato.
//
// La idea es no obligar a pasar por la pantalla de configuración para tocar lo que casi
// siempre se toca. Son TRES y sólo tres a propósito: Modelo y Pose quedan afuera por
// ahora (piden un picker con imágenes, que no entra en una celda de este tamaño).
//
// Se acomodan de a DOS por línea. Cada celda es igual a las otras —etiqueta arriba,
// valor abajo— porque son controles del mismo rango: ninguno manda sobre otro. La
// etiqueta va arriba y no a la izquierda por el ancho: el panel ocupa el 35% de la
// pantalla y, partido en dos columnas, no entran etiqueta y valor en la misma línea sin
// que el valor quede truncado a tres letras.
//
// Cuando la cantidad es IMPAR el último queda solo en su línea y se estira a lo ancho:
// media celda vacía al lado se lee como un control que falta cargar, no como una grilla
// que terminó.

import React from 'react';
import { ChevronRight, Sparkles } from 'lucide-react';
import { PromptRecientesButton } from './PromptRecientesButton';
import {
  RESOLUCION_DEFAULT,
  type ResolucionImagen,
} from '../../../lib/resolucionImagen';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';

/**
 * Opciones de Calidad. Los ids SON los de `resolucionImagen` y viajan al backend en
 * `image_size` (ADR-2026-08-25-resolucion-de-generacion-2k-4k): el control de Calidad y
 * la fila "Resolución" del panel de desktop son el MISMO ajuste, con el mismo estado y
 * el mismo costo.
 *
 * Nació con ids propios (`1080p` / `2k` / `4k`) y un upscale encadenado detrás, porque
 * cuando se diseñó este panel el request de imagen todavía no tenía parámetro de
 * resolución. Sí lo tenía: `principal` lo había agregado unos días antes y la rama no lo
 * vio. Generar nativo en 2K/4K es mejor que escalar después y no pide una segunda vuelta
 * al backend, así que la Calidad pasa a ser esto —
 * supersede ADR-2026-09-02-la-calidad-se-resuelve-con-el-upscale.
 *
 * Las etiquetas son CORTAS y no las de `OPCIONES_RESOLUCION` ("Estándar", "Alta (2K)"):
 * acá se dibujan en un segmentado de tres pastillas dentro de media celda, donde
 * "Máxima (4K)" no entra. La tabla larga sigue viva para la fila desplegable de desktop,
 * que tiene lugar para el detalle.
 */
export const CALIDAD_OPCIONES = [
  { id: '1K', label: '1080p' },
  { id: '2K', label: '2K' },
  { id: '4K', label: '4K' },
] as const;

export type CalidadImagen = ResolucionImagen;

export const CALIDAD_DEFAULT: CalidadImagen = RESOLUCION_DEFAULT;

export interface AjustesRapidosProductoProps {
  /** Calidad. OPCIONAL: sin `onCalidadChange` la celda no se dibuja — el modo Video no
   *  la ofrece (la resolución del video la fija el pipeline). */
  calidad?: CalidadImagen;
  onCalidadChange?: (v: CalidadImagen) => void;
  /** Texto del valor de Fondo — sale de `fondoSelectionLabel`. OPCIONAL por lo mismo:
   *  en Video la escena la arma el influencer, no hay fondo que elegir. */
  fondoLabel?: string;
  onOpenFondo?: () => void;
  /**
   * La celda de Fondo abre el PANEL LATERAL, igual que `formatoAbrePanelLateral`.
   *
   * La usa Fotos de producto, que elige su fondo ahí (liso o escena descrita) en vez de
   * en el sheet grande — ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo.
   */
  fondoAbrePanelLateral?: boolean;
  /** Texto del valor de Formato — sale de `getFormatLabel`. */
  formatoLabel: string;
  onOpenFormato: () => void;
  /**
   * La celda de Formato abre el PANEL LATERAL del modo, no un sheet.
   *
   * Marca el botón con `data-abre-panel-lateral`, que es lo que `PanelLateralModo` mira
   * para no tratar el tap como un "click afuera": sin eso el `mousedown` cierra el panel
   * y el `click` lo vuelve a abrir, y en el medio parpadea y reinicia su animación.
   */
  formatoAbrePanelLateral?: boolean;
  /** Panel angosto (la preview del paso 2): los controles pasan a UNA columna. A dos, en
   *  280px de panel, cada celda queda en ~110px y no entran ni las tres opciones de
   *  Calidad ni un nombre de fondo sin truncar a dos palabras. */
  compacto?: boolean;
  /** El contador de imágenes, como una celda más. No siempre existe: hay modos donde la
   *  cantidad no se elige (ver `ContadorImagenes` en `PresetDetailSheet`). */
  cantidad?: React.ReactNode;
  /**
   * Vuelve a la pantalla de Modelo + Pose (la columna de la derecha).
   *
   * Sólo llega con las TANDAS ocupando esa columna
   * (ADR-2026-08-20-varias-tandas-en-el-panel): ahí Modelo y Pose no están a la vista y
   * ésta es la única forma de volver a tocarlos sin esperar a que termine de generar.
   * Con la configuración a la vista no viene, y el bloque no se dibuja.
   */
  onCambiarModeloYPose?: () => void;
  /**
   * Instrucción libre para la IA. Sólo la manda "Automático"
   * (ADR-2026-08-21-automatico-con-el-diseno-nuevo): es el único modo sin pantalla de
   * configuración donde ponerla, porque todo lo demás lo decide la IA. Sin
   * `onInstruccionChange` la celda no se dibuja.
   */
  instruccion?: string;
  onInstruccionChange?: (v: string) => void;
  /**
   * Control de DURACIÓN del video, como bloque propio debajo de la grilla
   * (ADR-2026-08-21-video-con-el-diseno-nuevo).
   *
   * No va como celda: el control es la misma barra de siempre (`FillBarSliderV2`, con
   * su propia etiqueta y el valor a la derecha), que en media celda de 110px no entra.
   * Y no siempre existe: con guion escrito o grabado la duración la marca el guion, así
   * que el caller no manda nada y el bloque no se dibuja (ADR-528).
   */
  duracion?: React.ReactNode;
}

/**
 * Celda de la grilla: etiqueta arriba, valor abajo. Con `onClick` es un botón entero.
 *
 * EXPORTADA porque las columnas de Catálogo y de Modelo v2 dibujan los mismos ajustes y
 * tienen que verse igual que acá (ADR-2026-08-28-los-ajustes-se-ven-igual-en-los-tres-modos):
 * cada una tenía su propia forma —una fila de 48px con el nombre en negrita, dos botones
 * al ras sin chevron— y el mismo control se leía distinto en cada pantalla.
 */
export const CeldaAjuste: React.FC<{
  etiqueta: string;
  children: React.ReactNode;
  onClick?: () => void;
  /** Último de una cantidad impar: ocupa la línea entera. */
  solo?: boolean;
  /** Marca el botón como disparador del panel lateral — ver `formatoAbrePanelLateral`. */
  abrePanelLateral?: boolean;
}> = ({ etiqueta, children, onClick, solo, abrePanelLateral }) => {
  const ancho = solo ? 'col-span-2' : '';
  const contenido = (
    <>
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#3C3C43]/45">
        {etiqueta}
      </span>
      {children}
    </>
  );
  // Sin `onClick` la celda no es un botón: Calidad resuelve adentro, y un <button> que
  // envuelve a otros tres sería HTML inválido.
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      data-abre-panel-lateral={abrePanelLateral ? '' : undefined}
      className={`min-w-0 text-left px-3 py-2 rounded-xl bg-black/[0.035] hover:bg-black/[0.06] transition-colors ${ancho}`}
    >
      {contenido}
    </button>
  ) : (
    <div className={`min-w-0 px-3 py-2 rounded-xl bg-black/[0.035] ${ancho}`}>
      {contenido}
    </div>
  );
};

/** Valor de las celdas que abren un picker: texto + chevron. Exportado por lo mismo
 *  que `CeldaAjuste`. */
export const ValorConChevron: React.FC<{ texto: string }> = ({ texto }) => (
  <span className="mt-0.5 flex items-center justify-between gap-1">
    <span className="text-[13.5px] font-medium text-brand-black/90 truncate">
      {texto}
    </span>
    <ChevronRight className="w-4 h-4 text-[#3C3C43]/35 shrink-0" />
  </span>
);

/**
 * Calidad: las tres opciones a la vista, la elegida en blanco sobre el riel gris.
 *
 * Es un SEGMENTADO y no un valor que cicla al tocarlo ni un picker aparte: son tres
 * valores cortos que entran en la celda, y verlos todos dice de una qué se puede elegir
 * y cuánto margen queda para arriba. Exportado para que Catálogo y Modelo v2 usen éste
 * (ADR-2026-08-28-los-ajustes-se-ven-igual-en-los-tres-modos).
 */
export const CalidadSegmentada: React.FC<{
  calidad: CalidadImagen;
  onChange: (v: CalidadImagen) => void;
}> = ({ calidad, onChange }) => {
  const idx = CALIDAD_OPCIONES.findIndex((o) => o.id === calidad);
  const count = CALIDAD_OPCIONES.length;
  return (
    <span className="relative mt-1 flex items-center gap-0.5 p-0.5 rounded-lg bg-black/[0.05]">
      {/* Pastilla deslizante animada */}
      <span
        aria-hidden
        className="absolute top-0.5 bottom-0.5 rounded-md bg-white shadow-sm transition-transform duration-300 ease-out"
        style={{
          width: `calc(${100 / count}% - ${0.5 * (count - 1) / count}rem)`,
          transform: `translateX(calc(${idx * 100}% + ${idx * 2}px))`,
        }}
      />
      {CALIDAD_OPCIONES.map((opt) => {
        const activa = opt.id === calidad;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={`relative z-[1] flex-1 min-w-0 h-7 rounded-md text-[12px] font-semibold transition-colors duration-200 ${
              activa
                ? 'text-brand-black/90'
                : 'text-[#3C3C43]/55 hover:text-brand-black/80'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </span>
  );
};

/**
 * "Cambiar modelo y pose" — la vuelta a la configuración mientras la columna de la
 * derecha está mostrando las tandas (ADR-2026-08-20-cambiar-modelo-y-pose-destacado).
 *
 * NO es una celda más de la bandeja. Las celdas son ajustes que se resuelven ahí mismo
 * y se leen como estado ("Formato: Posteo"); esto es una invitación a hacer otra vuelta
 * —cambiar la combinación y volver a generar— y tiene que leerse como tal, con el color
 * de marca, un ícono y una bajada que lo dice con todas las letras. Como celda gris
 * número cinco pasaba desapercibida justo cuando es lo único accionable de la pantalla.
 */
const CambiarModeloYPose: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group w-full flex items-center gap-2.5 rounded-2xl border border-brand-primary/25 bg-brand-primary/[0.06] px-3 py-2.5 text-left transition-colors hover:border-brand-primary/45 hover:bg-brand-primary/[0.1]"
  >
    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white text-brand-primary shadow-sm">
      <Sparkles className="h-3.5 w-3.5" />
    </span>
    {/* Sin chevron: en el panel comprimido (280px) los 16px que se come parten el
        título en dos líneas, y la pastilla entera ya se lee como clickeable. */}
    <span className="min-w-0 flex-1">
      <span className="block text-[13px] font-semibold leading-tight text-brand-primary">
        Cambiar {palabraDeModeloActual().singular} y pose
      </span>
      <span className="mt-0.5 block text-[11.5px] leading-tight text-[#3C3C43]/60">
        Generá otra tanda sin esperar a la anterior
      </span>
    </span>
  </button>
);

/**
 * Instrucción libre: un campo de texto SIEMPRE desplegado
 * (ADR-2026-08-28-automatico-instruccion-desplegada, supersede
 * ADR-2026-08-21-automatico-con-el-diseno-nuevo en este punto).
 *
 * Antes era una celda "Instrucción → Añadir" que se abría al tocarla. Plegada no se
 * leía como un campo donde escribir sino como un ajuste más de la grilla, al lado de
 * Calidad y Formato, y en el único modo que la ofrece —Automático— escribir la
 * indicación es justamente lo que hace la diferencia entre una tanda genérica y una
 * dirigida. Se ve igual que en el modo Modelo —mismo textarea con marco y el mismo
 * placeholder—, para que la misma cosa se vea igual en los dos modos. Con DOS filas y no
 * cuatro: la indicación típica es media línea ("sobre mármol, luz cálida"), y el campo
 * alto se leía como un pedido de párrafo además de comerse el alto de la grilla de
 * productos. Crece solo si hace falta, porque el textarea scrollea.
 */
/**
 * EXPORTADO porque la columna compacta de Catálogo / Fotos de producto dibuja el mismo
 * campo (ADR-2026-09-04-fotos-de-producto-usa-la-columna-compacta-del-catalogo): es la
 * misma indicación, con el mismo historial, y una segunda copia se despegaría de ésta al
 * primer retoque.
 */
export const CampoInstruccion: React.FC<{
  valor: string;
  onChange: (v: string) => void;
  /** Alto en renglones. 2 en el pie de la grilla; el paso 2 de Automática, con la
   *  columna para él solo, pide 3 — los mismos que el campo de Modelo. */
  filas?: number;
}> = ({ valor, onChange, filas = 2 }) => (
  // `relative` + el `pb-11` del textarea: el botón se apoya DENTRO del campo, en la
  // esquina de abajo a la derecha, y el padding le reserva el lugar para que el texto
  // escrito nunca le pase por debajo. Es el mismo armado que ya usan los otros campos de
  // instrucción (ADR-2026-08-28-automatico-instruccion-con-recientes).
  <div className="relative">
    <textarea
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Agregá indicaciones extras para afinar el resultado"
      rows={filas}
      className="w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black/90 placeholder:text-brand-black/35 resize-none transition-colors"
    />
    {/* Mismo `tipo` que el resto de los campos de instrucción: el historial es UNO solo
        —lo que se escribió en Modelo aparece acá y al revés—, que es de lo que sirve
        (ADR-2026-08-12-historial-instrucciones-reusables). */}
    <PromptRecientesButton
      tipo="instruccion"
      onPick={onChange}
      titulo="Instrucciones usadas antes"
      className="absolute bottom-2.5 right-2.5"
    />
  </div>
);

export const AjustesRapidosProducto: React.FC<AjustesRapidosProductoProps> = ({
  calidad,
  onCalidadChange,
  fondoLabel,
  onOpenFondo,
  fondoAbrePanelLateral,
  formatoLabel,
  onOpenFormato,
  formatoAbrePanelLateral = false,
  compacto = false,
  cantidad,
  onCambiarModeloYPose,
  instruccion,
  onInstruccionChange,
  duracion,
}) => (
  <div className="px-6 pt-3 pb-4 flex flex-col gap-2.5">
    {/* Arriba de los ajustes y no al final: mientras se genera es la acción del
        momento, y al pie compite con el Generar que está justo abajo. */}
    {onCambiarModeloYPose && (
      <CambiarModeloYPose onClick={onCambiarModeloYPose} />
    )}
    {/* La instrucción va ARRIBA de la grilla y no al final: es lo único que se
        ESCRIBE y lo que más pesa en el resultado; abajo, después de Calidad y Formato,
        quedaba pegada al Generar y se leía como una nota al pie. Es además el mismo
        orden que el modo Modelo (instrucción → Calidad/Formato → Generar). */}
    {onInstruccionChange && (
      <CampoInstruccion valor={instruccion ?? ''} onChange={onInstruccionChange} />
    )}
    <div className={`grid gap-2 ${compacto ? 'grid-cols-1' : 'grid-cols-2'}`}>
    {calidad && onCalidadChange && (
      <CeldaAjuste etiqueta="Calidad">
        <CalidadSegmentada calidad={calidad} onChange={onCalidadChange} />
      </CeldaAjuste>
    )}

    {fondoLabel !== undefined && onOpenFondo && (
      <CeldaAjuste
        etiqueta="Fondo"
        onClick={onOpenFondo}
        abrePanelLateral={fondoAbrePanelLateral}
      >
        <ValorConChevron texto={fondoLabel} />
      </CeldaAjuste>
    )}

    {/* Formato queda solo en su línea cuando la cuenta de celdas es IMPAR: con Calidad,
        Fondo y contador son cuatro y la grilla cierra en dos filas de dos; sin contador
        —o en Video, donde Calidad y Fondo no van— queda impar y se estira. En una
        columna el `solo` sobra: su `col-span-2` rompería una grilla de una sola. */}
    <CeldaAjuste
      etiqueta="Formato"
      onClick={onOpenFormato}
      abrePanelLateral={formatoAbrePanelLateral}
      solo={
        !compacto &&
        ((calidad ? 1 : 0) +
          (fondoLabel !== undefined ? 1 : 0) +
          (cantidad ? 1 : 0)) %
          2 ===
          0
      }
    >
      <ValorConChevron texto={formatoLabel} />
    </CeldaAjuste>

      {cantidad && (
        <CeldaAjuste etiqueta="Imágenes">{cantidad}</CeldaAjuste>
      )}
    </div>

    {/* La barra de duración, a lo ancho: es el único control de acá que no cabe en
        media celda. */}
    {duracion}

  </div>
);
